import React, { useState, useEffect, useCallback } from "react";
import { useWebSocketContext } from "../../contexts/WebSocketContext";
import {
  InitiativeEntry,
  InitiativeUpdateData,
  HealthUpdateData,
  StatusEffectData,
} from "../../types/websocket";

interface InitiativeTrackerProps {
  className?: string;
  sessionId?: string;
  isDM?: boolean;
}

export const InitiativeTracker: React.FC<InitiativeTrackerProps> = ({
  className = "",
  sessionId,
  isDM = false,
}) => {
  const { isConnected, sendInitiativeUpdate, updateCombatState, getMessagesByType } =
    useWebSocketContext();

  const [entries, setEntries] = useState<InitiativeEntry[]>([]);
  const [isTracking, setIsTracking] = useState(true);
  const [currentTurn, setCurrentTurn] = useState(0);
  const [round, setRound] = useState(1);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newEntryName, setNewEntryName] = useState("");
  const [newEntryInitiative, setNewEntryInitiative] = useState("");
  const [newEntryIsPlayer, setNewEntryIsPlayer] = useState(true);
  const [newEntryHP, setNewEntryHP] = useState("");
  const [newEntryMaxHP, setNewEntryMaxHP] = useState("");

  // Sync from the DM's latest full-state initiative_update broadcast for this session.
  // The DM broadcasts full state on every mutation (below), so this keeps every
  // participant's tracker in lockstep. Keyed on the message id to avoid render loops.
  const initiativeMessages = getMessagesByType("initiative_update", sessionId);
  const latestInit = initiativeMessages[initiativeMessages.length - 1];
  useEffect(() => {
    if (!latestInit?.data) return;
    const data = latestInit.data as InitiativeUpdateData;
    if (Array.isArray(data.entries)) setEntries(data.entries);
    if (typeof data.currentTurn === "number") setCurrentTurn(data.currentTurn);
    if (typeof data.round === "number") setRound(data.round);
    if (typeof data.isTracking === "boolean") setIsTracking(data.isTracking);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestInit?.id, latestInit?.timestamp]);

  // Apply incremental health/status updates that other components may broadcast.
  const healthMessages = getMessagesByType("health_update", sessionId);
  const latestHealth = healthMessages[healthMessages.length - 1];
  useEffect(() => {
    if (!latestHealth?.data) return;
    const data = latestHealth.data as HealthUpdateData;
    setEntries((prev) =>
      prev.map((entry) =>
        entry.id === data.entryId ? { ...entry, hp: data.hp, maxHp: data.maxHp } : entry
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestHealth?.id, latestHealth?.timestamp]);

  const statusMessages = getMessagesByType("status_effect", sessionId);
  const latestStatus = statusMessages[statusMessages.length - 1];
  useEffect(() => {
    if (!latestStatus?.data) return;
    const data = latestStatus.data as StatusEffectData;
    setEntries((prev) =>
      prev.map((entry) =>
        entry.id === data.entryId ? { ...entry, conditions: data.conditions } : entry
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestStatus?.id, latestStatus?.timestamp]);

  // Broadcast full initiative state to the session. DM only — players stay read-only
  // and are kept in sync by the effect above. Callers pass the freshly computed
  // values so we never broadcast stale state captured in a closure.
  const broadcastState = useCallback(
    (next: {
      entries: InitiativeEntry[];
      currentTurn: number;
      round: number;
      isTracking: boolean;
    }) => {
      if (sessionId && isConnected && isDM) {
        sendInitiativeUpdate(
          sessionId,
          next.entries,
          next.currentTurn,
          next.round,
          next.isTracking
        );
      }
    },
    [sessionId, isConnected, isDM, sendInitiativeUpdate]
  );

  const nextTurn = () => {
    if (entries.length === 0) return;
    const nextIndex = (currentTurn + 1) % entries.length;
    const newRound = nextIndex === 0 ? round + 1 : round;

    setCurrentTurn(nextIndex);
    setRound(newRound);

    const updatedEntries = entries.map((entry, index) => ({
      ...entry,
      isActive: index === nextIndex,
    }));
    setEntries(updatedEntries);

    broadcastState({
      entries: updatedEntries,
      currentTurn: nextIndex,
      round: newRound,
      isTracking,
    });
  };

  const prevTurn = () => {
    if (entries.length === 0) return;
    const prevIndex = currentTurn === 0 ? entries.length - 1 : currentTurn - 1;
    const newRound = currentTurn === 0 && round > 1 ? round - 1 : round;

    setCurrentTurn(prevIndex);
    setRound(newRound);

    const updatedEntries = entries.map((entry, index) => ({
      ...entry,
      isActive: index === prevIndex,
    }));
    setEntries(updatedEntries);

    broadcastState({
      entries: updatedEntries,
      currentTurn: prevIndex,
      round: newRound,
      isTracking,
    });
  };

  const sortByInitiative = () => {
    const sorted = [...entries].sort((a, b) => b.initiative - a.initiative);

    const updatedEntries = sorted.map((entry, index) => ({
      ...entry,
      isActive: index === 0,
    }));
    setEntries(updatedEntries);
    setCurrentTurn(0);

    broadcastState({ entries: updatedEntries, currentTurn: 0, round, isTracking });
  };

  const toggleTracking = () => {
    const newTracking = !isTracking;
    setIsTracking(newTracking);

    if (sessionId && isConnected) {
      updateCombatState(sessionId, newTracking ? "active" : "paused");
    }

    broadcastState({ entries, currentTurn, round, isTracking: newTracking });
  };

  const handleAddCombatant = () => {
    setShowAddDialog(true);
  };

  const addCombatant = () => {
    if (!newEntryName || !newEntryInitiative) return;

    const newEntry: InitiativeEntry = {
      id: `entry-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: newEntryName,
      initiative: parseInt(newEntryInitiative, 10),
      isPlayer: newEntryIsPlayer,
      isActive: false,
      hp: newEntryHP ? parseInt(newEntryHP, 10) : undefined,
      maxHp: newEntryMaxHP ? parseInt(newEntryMaxHP, 10) : undefined,
      conditions: [],
    };

    const updatedEntries = [...entries, newEntry];
    setEntries(updatedEntries);

    setNewEntryName("");
    setNewEntryInitiative("");
    setNewEntryIsPlayer(true);
    setNewEntryHP("");
    setNewEntryMaxHP("");
    setShowAddDialog(false);

    broadcastState({ entries: updatedEntries, currentTurn, round, isTracking });
  };

  const removeCombatant = (entryId: string) => {
    const updatedEntries = entries.filter((entry) => entry.id !== entryId);
    const newTurn =
      currentTurn >= updatedEntries.length && updatedEntries.length > 0 ? 0 : currentTurn;
    setEntries(updatedEntries);
    setCurrentTurn(newTurn);

    broadcastState({ entries: updatedEntries, currentTurn: newTurn, round, isTracking });
  };

  const endCombat = () => {
    setEntries([]);
    setCurrentTurn(0);
    setRound(1);
    setIsTracking(false);

    if (sessionId && isConnected) {
      updateCombatState(sessionId, "inactive");
    }

    broadcastState({ entries: [], currentTurn: 0, round: 1, isTracking: false });
  };

  const rollInitiative = () => {
    const updatedEntries = entries.map((entry) => ({
      ...entry,
      initiative: Math.floor(Math.random() * 20) + 1,
    }));

    const sorted = updatedEntries.sort((a, b) => b.initiative - a.initiative);

    const withActive = sorted.map((entry, index) => ({
      ...entry,
      isActive: index === 0,
    }));

    setEntries(withActive);
    setCurrentTurn(0);

    broadcastState({ entries: withActive, currentTurn: 0, round, isTracking });
  };

  return (
    <div
      className={`bg-gray-800 rounded-lg border border-gray-600 p-4 ${className}`}
      data-testid="initiative-tracker"
      role="region"
      aria-label="Turn order tracker"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold text-lg">Turn Order</h3>
        <div className="flex items-center space-x-2" data-testid="initiative-controls">
          <button
            onClick={toggleTracking}
            className={`px-2 py-1 rounded text-xs ${
              isTracking ? "bg-green-600 text-white" : "bg-gray-600 text-gray-300"
            }`}
            data-testid="toggle-tracking-button"
            aria-label={isTracking ? "Pause initiative tracking" : "Resume initiative tracking"}
          >
            {isTracking ? "Active" : "Paused"}
          </button>
          <button
            onClick={sortByInitiative}
            className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
            data-testid="sort-initiative-button"
            aria-label="Sort entries by initiative order"
          >
            Sort
          </button>
          {isDM && (
            <button
              onClick={rollInitiative}
              className="px-2 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-700"
              data-testid="roll-initiative-button"
              aria-label="Roll initiative for all combatants"
            >
              Roll All
            </button>
          )}
        </div>
      </div>

      {isTracking && entries.length > 0 && (
        <div
          className="flex items-center justify-between mb-4 p-2 bg-gray-700 rounded"
          data-testid="turn-controls"
        >
          <button
            onClick={prevTurn}
            className="px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-500 text-sm"
            data-testid="prev-turn-button"
            aria-label="Go to previous turn"
          >
            ← Prev
          </button>
          <span
            className="text-white text-sm"
            data-testid="turn-counter"
            aria-label={`Turn ${currentTurn + 1} of ${entries.length}`}
          >
            Turn {currentTurn + 1} of {entries.length}
          </span>
          <button
            onClick={nextTurn}
            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
            data-testid="next-turn-button"
            aria-label="Go to next turn"
          >
            Next →
          </button>
        </div>
      )}

      <div className="space-y-2" data-testid="initiative-entries">
        {entries.length === 0 ? (
          <div className="text-gray-400 text-center py-4">No combatants in initiative</div>
        ) : (
          entries.map((entry, index) => (
            <div
              key={entry.id}
              className={`p-3 rounded-lg border transition-all ${
                entry.isActive && isTracking
                  ? "border-blue-500 bg-blue-900/20"
                  : "border-gray-600 bg-gray-700"
              }`}
              data-testid={`initiative-entry-${entry.id}`}
              role="listitem"
              aria-label={`${entry.name} - Initiative ${entry.initiative} - ${entry.isActive && isTracking ? "Active turn" : "Waiting"}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <span
                      className="text-gray-400 text-sm w-6"
                      data-testid={`entry-position-${entry.id}`}
                    >
                      {index + 1}
                    </span>
                    <div
                      className={`w-3 h-3 rounded-full ${
                        entry.isPlayer ? "bg-blue-500" : "bg-red-500"
                      }`}
                      data-testid={`entry-type-indicator-${entry.id}`}
                      aria-label={entry.isPlayer ? "Player character" : "Non-player character"}
                    />
                  </div>
                  <div>
                    <div className="text-white font-medium" data-testid={`entry-name-${entry.id}`}>
                      {entry.name}
                    </div>
                    {entry.conditions && entry.conditions.length > 0 && (
                      <div
                        className="flex space-x-1 mt-1"
                        data-testid={`entry-conditions-${entry.id}`}
                      >
                        {entry.conditions.map((condition, idx) => (
                          <span
                            key={idx}
                            className="px-1 py-0.5 bg-yellow-600 text-yellow-100 text-xs rounded"
                            data-testid={`condition-${entry.id}-${idx}`}
                          >
                            {condition}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="text-right flex items-center space-x-3">
                  <div data-testid={`entry-stats-${entry.id}`}>
                    <div
                      className="text-white font-semibold"
                      data-testid={`entry-initiative-${entry.id}`}
                    >
                      Init: {entry.initiative}
                    </div>
                    {entry.hp !== undefined && (
                      <div className="text-sm">
                        <span
                          className={
                            entry.hp <= (entry.maxHp || 0) / 4 ? "text-red-400" : "text-gray-400"
                          }
                          data-testid={`entry-hp-${entry.id}`}
                          aria-label={`Health points: ${entry.hp} of ${entry.maxHp}`}
                        >
                          HP: {entry.hp}/{entry.maxHp}
                        </span>
                      </div>
                    )}
                  </div>
                  {isDM && (
                    <button
                      onClick={() => removeCombatant(entry.id)}
                      className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                      data-testid={`remove-entry-${entry.id}`}
                      aria-label={`Remove ${entry.name} from initiative`}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>

              {/* HP Bar */}
              {entry.hp !== undefined && entry.maxHp && (
                <div className="mt-2" data-testid={`entry-hp-bar-${entry.id}`}>
                  <div className="w-full bg-gray-600 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        entry.hp <= entry.maxHp / 4
                          ? "bg-red-500"
                          : entry.hp <= entry.maxHp / 2
                            ? "bg-yellow-500"
                            : "bg-green-500"
                      }`}
                      style={{ width: `${(entry.hp / entry.maxHp) * 100}%` }}
                      data-testid={`hp-bar-fill-${entry.id}`}
                      aria-label={`Health bar showing ${Math.round((entry.hp / entry.maxHp) * 100)}% health`}
                    />
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <div className="mt-4 space-y-2" data-testid="initiative-actions">
        <button
          onClick={handleAddCombatant}
          className="w-full px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm"
          data-testid="add-combatant-button"
          aria-label="Add new combatant to initiative"
        >
          Add Combatant
        </button>
        {entries.length > 0 && (
          <button
            onClick={endCombat}
            className="w-full px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm"
            data-testid="end-combat-button"
            aria-label="End combat and clear initiative"
          >
            End Combat
          </button>
        )}
      </div>

      {/* Add Combatant Dialog */}
      {showAddDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-white font-semibold text-lg mb-4">Add Combatant</h3>
            <div className="space-y-3">
              <div>
                <label className="text-gray-300 text-sm block mb-1">Name</label>
                <input
                  type="text"
                  value={newEntryName}
                  onChange={(e) => setNewEntryName(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                  placeholder="Character name"
                />
              </div>
              <div>
                <label className="text-gray-300 text-sm block mb-1">Initiative</label>
                <input
                  type="number"
                  value={newEntryInitiative}
                  onChange={(e) => setNewEntryInitiative(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                  placeholder="Initiative roll"
                />
              </div>
              <div className="flex space-x-3">
                <div className="flex-1">
                  <label className="text-gray-300 text-sm block mb-1">HP</label>
                  <input
                    type="number"
                    value={newEntryHP}
                    onChange={(e) => setNewEntryHP(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                    placeholder="Current HP"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-gray-300 text-sm block mb-1">Max HP</label>
                  <input
                    type="number"
                    value={newEntryMaxHP}
                    onChange={(e) => setNewEntryMaxHP(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                    placeholder="Max HP"
                  />
                </div>
              </div>
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={newEntryIsPlayer}
                    onChange={(e) => setNewEntryIsPlayer(e.target.checked)}
                    className="rounded border-gray-600 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-300 text-sm">Player Character</span>
                </label>
              </div>
            </div>
            <div className="flex space-x-2 mt-6">
              <button
                onClick={addCombatant}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Add
              </button>
              <button
                onClick={() => setShowAddDialog(false)}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
